import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { C, FONT, R, SP } from '../theme/tokens';
import { clearHistory, HistoryEntry, loadHistory } from '../utils/history';

type Nav = {
  navigate: (screen: string, params?: any) => void;
  addListener: (event: string, cb: () => void) => () => void;
};

function formatWhen(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return `Today · ${time}`;
  const date = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  return `${date} · ${time}`;
}

export default function HistoryScreen({ navigation }: { navigation: Nav }) {
  const [items, setItems] = useState<HistoryEntry[]>([]);
  const [query, setQuery] = useState('');

  const refresh = useCallback(async () => {
    setItems(await loadHistory());
  }, []);

  useEffect(() => {
    const unsub = navigation.addListener('focus', refresh);
    return unsub;
  }, [navigation, refresh]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((e) => {
      if (e.title.toLowerCase().includes(q)) return true;
      return e.result.some(
        (r) =>
          r.label.toLowerCase().includes(q) || r.value.toLowerCase().includes(q),
      );
    });
  }, [items, query]);

  const onClear = async () => {
    await clearHistory();
    await refresh();
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <View style={styles.inner}>
        <View style={styles.header}>
          <View style={styles.eyebrowRow}>
            <View style={styles.eyebrowBar} />
            <Text style={styles.eyebrowText}>HISTORY</Text>
          </View>
          <Text style={styles.title}>Saved results</Text>
          <Text style={styles.subtitle}>
            Every calculation is logged on this device. Stored locally — nothing
            leaves your browser.
          </Text>
        </View>

        <View style={styles.controlsRow}>
          <View style={styles.search}>
            <Ionicons name="search" size={14} color={C.dim} />
            <TextInput
              value={query}
              onChangeText={setQuery}
              placeholder="Search results"
              placeholderTextColor={C.dim}
              style={styles.searchInput}
            />
          </View>
          <Pressable
            onPress={onClear}
            style={({ pressed }) => [styles.clearBtn, pressed && styles.pressed]}
            accessibilityRole="button"
            accessibilityLabel="Clear history"
          >
            <Ionicons name="trash-outline" size={16} color={C.ink2} />
          </Pressable>
        </View>

        {filtered.length === 0 ? (
          <View style={styles.emptyCard}>
            <Text style={styles.emptyTitle}>No saved results yet</Text>
            <Text style={styles.emptyText}>
              Run any calculator and the result will be logged here automatically.
            </Text>
          </View>
        ) : (
          filtered.map((entry, idx) => {
            const hero = entry.result.find((r) => !r.label.toLowerCase().includes('note')) ??
              entry.result[0];
            return (
              <Pressable
                key={`${entry.ts}-${idx}`}
                style={({ pressed }) => [styles.entry, pressed && styles.pressed]}
                onPress={() =>
                  navigation.navigate(entry.category, { screen: entry.route })
                }
              >
                <View style={styles.entryHeaderRow}>
                  <Text style={styles.entryCategory}>{entry.category.toUpperCase()}</Text>
                  <Text style={styles.entryWhen}>{formatWhen(entry.ts)}</Text>
                </View>
                <Text style={styles.entryTitle}>{entry.title}</Text>
                {hero ? (
                  <Text style={styles.entryResult}>
                    <Text style={styles.entryLabel}>{hero.label}: </Text>
                    {hero.value}
                    {hero.unit ? ` ${hero.unit}` : ''}
                  </Text>
                ) : null}
                {entry.result.length > 1 ? (
                  <Text style={styles.entryMore}>
                    +{entry.result.length - 1} more value{entry.result.length - 1 === 1 ? '' : 's'}
                  </Text>
                ) : null}
              </Pressable>
            );
          })
        )}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: C.bg },
  content: { padding: SP.md, paddingBottom: SP.xl },
  inner: { width: '100%', maxWidth: 980, alignSelf: 'center' },

  header: { marginBottom: SP.md },
  eyebrowRow: { flexDirection: 'row', alignItems: 'center', marginBottom: SP.xs },
  eyebrowBar: {
    width: 4,
    height: 11,
    backgroundColor: C.accent,
    borderRadius: 1,
    marginRight: SP.sm,
  },
  eyebrowText: { fontFamily: FONT.mono, fontSize: 11, color: C.dim, letterSpacing: 0.8 },
  title: { fontFamily: FONT.sansBold, fontSize: 26, color: C.ink, letterSpacing: -0.3 },
  subtitle: {
    fontFamily: FONT.sans,
    fontSize: 13,
    color: C.ink2,
    marginTop: SP.xs + 2,
    lineHeight: 19,
  },

  controlsRow: { flexDirection: 'row', gap: SP.sm, marginBottom: SP.md },
  search: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: SP.sm,
    backgroundColor: C.panel,
    borderRadius: R.field,
    borderWidth: 1,
    borderColor: C.edge,
    paddingHorizontal: SP.sm + 4,
    height: 40,
  },
  searchInput: {
    flex: 1,
    fontFamily: FONT.sans,
    fontSize: 14,
    color: C.ink,
    paddingVertical: 0,
  },
  clearBtn: {
    width: 40,
    height: 40,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: C.edge,
    backgroundColor: C.panel,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pressed: { opacity: 0.85 },

  emptyCard: {
    backgroundColor: C.panel,
    borderRadius: R.card,
    borderWidth: 1,
    borderColor: C.edge,
    padding: SP.lg,
    alignItems: 'center',
  },
  emptyTitle: {
    fontFamily: FONT.sansMed,
    fontSize: 15,
    color: C.ink,
    marginBottom: SP.xs,
  },
  emptyText: {
    fontFamily: FONT.sans,
    fontSize: 13,
    color: C.ink2,
    textAlign: 'center',
    lineHeight: 19,
  },

  entry: {
    backgroundColor: C.panel,
    borderRadius: R.card,
    borderWidth: 1,
    borderColor: C.edge,
    padding: SP.md,
    marginBottom: SP.sm + 2,
  },
  entryHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: SP.xs,
  },
  entryCategory: {
    fontFamily: FONT.mono,
    fontSize: 10,
    color: C.dim,
    letterSpacing: 0.8,
  },
  entryWhen: { fontFamily: FONT.mono, fontSize: 11, color: C.dim },
  entryTitle: {
    fontFamily: FONT.sansMed,
    fontSize: 15,
    color: C.ink,
    marginBottom: SP.xs,
  },
  entryResult: { fontFamily: FONT.mono, fontSize: 14, color: C.ink },
  entryLabel: { color: C.ink2 },
  entryMore: {
    fontFamily: FONT.sans,
    fontSize: 11,
    color: C.dim,
    marginTop: 4,
  },
});
